/**
 * AuthContext — source-agnostic auth state for the terminal.
 *
 * The same context shape is populated whether tokens arrive via the OAuth
 * authorization-code flow or a postMessage handshake from a native webview
 * host (API-4347). Downstream consumers (KMP bearer token provider, API calls,
 * protected routes) only need to read `accessToken`/`isAuthenticated`.
 *
 * Tokens are kept in memory only. Persisting across full page reloads is
 * explicitly a session-lifecycle concern (API-4351).
 */
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import type { ReactNode } from 'react';
import { detectMode } from '@src/config/webviewDetection';

export type AuthMode = 'webview' | 'browser' | 'unknown';

/**
 * Boot phase tracks the initial app lifecycle before authentication:
 * - 'detecting': mode detection in progress (webview vs browser)
 * - 'ready': mode determined, normal auth flow can proceed
 */
export type BootPhase = 'detecting' | 'ready';

export interface AuthState {
    accessToken: string | null;
    refreshToken: string | null;
    /** Token expiry as ISO 8601 string, or null when unknown. */
    expiresAt: string | null;
    mode: AuthMode;
    isAuthenticated: boolean;
    bootPhase: BootPhase;
    isSessionExpired: boolean;
}

export interface SetTokensInput {
    accessToken: string;
    refreshToken?: string | null;
    expiresAt?: string | null;
    mode: AuthMode;
}

export interface AuthContextValue extends AuthState {
    setTokens: (tokens: SetTokensInput) => void;
    clearAuth: () => void;
    markSessionExpired: () => void;
    /** Mark boot as ready (used after webview detection timeout). */
    markBootReady: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const INITIAL_STATE: AuthState = {
    accessToken: null,
    refreshToken: null,
    expiresAt: null,
    mode: 'unknown',
    isAuthenticated: false,
    bootPhase: 'detecting',
    isSessionExpired: false
};

/**
 * How long to wait for a postMessage init before falling back to browser mode.
 */
const WEBVIEW_INIT_TIMEOUT_MS = 1500;

export function AuthProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<AuthState>(INITIAL_STATE);
    const bootTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const detected = detectMode();

        if (detected === 'browser') {
            // No webview bridge detected — immediately ready for OAuth flow.
            setState(prev => ({
                ...prev,
                mode: 'browser',
                bootPhase: 'ready'
            }));
        } else {
            // Webview or unknown — wait briefly for postMessage init.
            setState(prev => ({ ...prev, mode: detected }));
            bootTimerRef.current = setTimeout(() => {
                setState(prev => {
                    if (prev.bootPhase === 'detecting') {
                        return { ...prev, bootPhase: 'ready' };
                    }
                    return prev;
                });
            }, WEBVIEW_INIT_TIMEOUT_MS);
        }

        return () => {
            if (bootTimerRef.current) clearTimeout(bootTimerRef.current);
        };
    }, []);

    const setTokens = useCallback((tokens: SetTokensInput) => {
        if (bootTimerRef.current) {
            clearTimeout(bootTimerRef.current);
            bootTimerRef.current = null;
        }
        setState({
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken ?? null,
            expiresAt: tokens.expiresAt ?? null,
            mode: tokens.mode,
            isAuthenticated: true,
            bootPhase: 'ready',
            isSessionExpired: false
        });
    }, []);

    const clearAuth = useCallback(() => {
        setState({ ...INITIAL_STATE, bootPhase: 'ready', mode: 'unknown' });
    }, []);

    const markSessionExpired = useCallback(() => {
        setState(prev => ({
            ...INITIAL_STATE,
            bootPhase: 'ready',
            mode: prev.mode,
            isSessionExpired: true
        }));
    }, []);

    const markBootReady = useCallback(() => {
        if (bootTimerRef.current) {
            clearTimeout(bootTimerRef.current);
            bootTimerRef.current = null;
        }
        setState(prev => ({ ...prev, bootPhase: 'ready' }));
    }, []);

    const value = useMemo<AuthContextValue>(
        () => ({
            ...state,
            setTokens,
            clearAuth,
            markSessionExpired,
            markBootReady
        }),
        [state, setTokens, clearAuth, markSessionExpired, markBootReady]
    );

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within an <AuthProvider>');
    }
    return ctx;
}
